/**
 * BilagorTab — the "Bilagor" surface in the contract editor (per ADR-0028,
 * 2026-05-26 addendum: per-bilaga inner tabs).
 *
 * The top-level editor strip keeps a single "Bilagor (N)" tab. Inside it,
 * this component renders its OWN inner tab strip — one tab per bilaga plus a
 * trailing "+ Lägg till bilaga" tab — so each bilaga is previewed in place
 * instead of opening in a new browser tab (the old "bilagan försvinner"
 * complaint, T-03):
 *
 *   - declared slot (Pattern A): always a tab, even unfilled. An empty one
 *     shows an upload prompt; required-but-missing gets a red dot.
 *   - uploaded PDF: embedded inline (iframe over the presigned URL), with
 *     an "open in new tab" escape hatch.
 *   - template bilaga: a two-pane editor (fields + live preview), the same
 *     shape as the Avtal page.
 *   - "+ Lägg till bilaga": the create surface (PDF upload + från mall). It
 *     is the default tab when no bilagor exist yet.
 *
 * Auto-numbering (T-02) is unchanged from ADR-0028: slot-bound rows show
 * their template's fixed "Bilaga N"; freeform rows show
 * "Bilaga {slotCount + position + 1}". Names stay editable inline.
 *
 * This inner tab system is deliberately NOT shared with the Påminnelser /
 * Granskning meta-tabs — those stay on the top strip.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
} from 'react';
import {
  Paperclip,
  Trash2,
  ChevronUp,
  ChevronDown,
  Upload,
  ExternalLink,
  Check,
  X,
  Pencil,
  FilePlus,
  RotateCw,
  Plus,
  AlertCircle,
  Lock,
} from 'lucide-react';
import {
  DocumentMetaProvider,
  RENDERERS,
  TEMPLATE_REGISTRY,
  applyDerivedFromParent,
  evaluateVisibilityRule,
  getTemplateVersion,
  isDerivedLocked,
  overrideKey,
  resolveObjektNr,
  computeSectionNumbers,
  readCustomSections,
  templateSupportsCustomSections,
  CUSTOM_SECTIONS_KEY,
  type AttachmentSlot,
  type TemplateModule,
  type VariableField,
} from '@hyranai/regta-templates';
import { tenantApi, type ContractAttachment } from '../api.js';
import { CustomSectionsEditor } from './CustomSectionsEditor.js';
import {
  BILAGA_ACCEPTED_MIME,
  BILAGA_MAX_BYTES,
  createBilagaFromTemplate,
  uploadBilaga,
} from '../lib/bilaga-create.js';
import { dataTableXlsxIO } from '../lib/data-table-xlsx.js';
import { locateAnchor } from '../lib/locate-anchor.js';

const MAX_BYTES = BILAGA_MAX_BYTES;
const ACCEPTED_MIME = BILAGA_ACCEPTED_MIME;

function formatBytes(bytes: number | null): string {
  if (bytes === null || bytes === undefined) return '';
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  const kb = bytes / 1024;
  return `${kb.toFixed(0)} kB`;
}

/** An entry in the inner tab strip. */
type InnerTab =
  | {
      key: string;
      kind: 'slot';
      slot: AttachmentSlot;
      attachment: ContractAttachment | null;
      number: number;
      /** Required + visible + unfilled — a proactive warning (amber). */
      missing: boolean;
      /** Also flagged by a failed promote — a hard blocker (red). */
      blocking: boolean;
    }
  | {
      key: string;
      kind: 'freeform';
      attachment: ContractAttachment;
      number: number;
      /** Position within the freeform sub-list (for reorder). */
      freeIndex: number;
    }
  | { key: string; kind: 'add' };

export type TemplateEditorRenderer = (
  attachment: ContractAttachment,
  ctx: { disabled: boolean; onChanged: () => Promise<void> | void },
) => ReactElement | null;

export function BilagorTab({
  token,
  contractId,
  parentTemplateKey,
  parentValues,
  attachments,
  visibleSlots,
  missingSlotIds,
  locateSlot,
  locateAttachment,
  onChanged,
  disabled,
  renderTemplateEditor,
}: {
  token: string;
  contractId: string;
  parentTemplateKey: string;
  /** Parent contract `values` — drives objektNr resolution in template previews. */
  parentValues: Record<string, unknown>;
  attachments: ReadonlyArray<ContractAttachment>;
  visibleSlots: ReadonlyArray<AttachmentSlot>;
  missingSlotIds?: ReadonlyArray<string>;
  /** REGTA-49 — request to open a slot's inner tab; `seq` bumps per click
   *  so repeat clicks on the same entry re-activate. */
  locateSlot?: { slotId: string; seq: number } | null;
  /** Opens a bilaga's inner tab (field navigation); `seq` bumps per request. */
  locateAttachment?: { id: string; seq: number } | null;
  onChanged: () => Promise<void> | void;
  disabled: boolean;
  /**
   * Overrides the editor of a template bilaga (the beta lane renders each
   * template bilaga as its own IR document). Return null to fall back to
   * the legacy editor.
   */
  renderTemplateEditor?: TemplateEditorRenderer;
}): ReactElement {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [active, setActive] = useState<string>('add');

  const slotBound = useMemo(
    () =>
      new Map(
        attachments
          .filter((a) => a.slotId !== null)
          .map((a) => [a.slotId as string, a] as const),
      ),
    [attachments],
  );
  const freeformAttachments = useMemo(
    () =>
      [...attachments]
        .filter((a) => a.slotId === null)
        .sort((a, b) => a.ordinal - b.ordinal),
    [attachments],
  );

  const tabs = useMemo<InnerTab[]>(() => {
    const out: InnerTab[] = [];
    const sortedSlots = [...visibleSlots].sort(
      (a, b) => a.fixedNumber - b.fixedNumber,
    );
    for (const slot of sortedSlots) {
      const bound = slotBound.get(slot.id) ?? null;
      out.push({
        key: `slot:${slot.id}`,
        kind: 'slot',
        slot,
        attachment: bound,
        // Server-resolved number when bound; the slot's own fixed number
        // as the placeholder while unfilled (ADR-0028 addendum 2026-05-27).
        number: bound?.number ?? slot.fixedNumber,
        missing: !bound && !!slot.required,
        blocking: !bound && !!slot.required && !!missingSlotIds?.includes(slot.id),
      });
    }
    freeformAttachments.forEach((a, i) => {
      out.push({
        key: `att:${a.id}`,
        kind: 'freeform',
        attachment: a,
        number: a.number ?? visibleSlots.length + i + 1,
        freeIndex: i,
      });
    });
    out.push({ key: 'add', kind: 'add' });
    return out;
  }, [visibleSlots, slotBound, freeformAttachments, missingSlotIds]);

  // First mount: land on the first real bilaga if any exist, else the
  // create view.
  useEffect(() => {
    setActive((cur) => {
      if (cur !== 'add') return cur;
      const first = tabs.find((t) => t.kind !== 'add');
      return first ? first.key : 'add';
    });
    // Only run once on mount; later switches are explicit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!locateSlot) return;
    setActive(`slot:${locateSlot.slotId}`);
  }, [locateSlot]);

  useEffect(() => {
    if (!locateAttachment) return;
    const a = attachments.find((x) => x.id === locateAttachment.id);
    if (a) setActive(a.slotId !== null ? `slot:${a.slotId}` : `att:${a.id}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locateAttachment]);

  // Keep the active key pointing at a tab that still exists (e.g. the active
  // bilaga was removed). Fall back to the first tab, then the add view.
  useEffect(() => {
    if (tabs.some((t) => t.key === active)) return;
    const first = tabs.find((t) => t.kind !== 'add');
    setActive(first ? first.key : 'add');
  }, [tabs, active]);

  const handleFile = useCallback(
    async (
      file: File,
      slotId?: string | null,
      displayName?: string,
    ): Promise<ContractAttachment | null> => {
      setBusy(true);
      setErr(null);
      try {
        const created = await uploadBilaga(contractId, file, {
          displayName,
          slotId,
        });
        await onChanged();
        return created;
      } catch (e) {
        setErr((e as Error).message);
        return null;
      } finally {
        setBusy(false);
      }
    },
    [contractId, onChanged],
  );

  const uploadFreeform = useCallback(
    async (file: File) => {
      const created = await handleFile(file);
      if (created) setActive(`att:${created.id}`);
    },
    [handleFile],
  );

  const uploadToSlot = useCallback(
    async (slot: AttachmentSlot, file: File) => {
      const created = await handleFile(file, slot.id, slot.label);
      if (created) setActive(`slot:${slot.id}`);
    },
    [handleFile],
  );

  const remove = useCallback(
    async (id: string) => {
      setBusy(true);
      setErr(null);
      try {
        await tenantApi.contract_attachments.remove({ id });
        await onChanged();
      } catch (e) {
        setErr((e as Error).message);
      } finally {
        setBusy(false);
      }
    },
    [onChanged],
  );

  const reorderFreeform = useCallback(
    async (from: number, to: number) => {
      if (to < 0 || to >= freeformAttachments.length) return;
      const fullOrdered = [...attachments].sort((a, b) => a.ordinal - b.ordinal);
      const a = freeformAttachments[from]!.id;
      const b = freeformAttachments[to]!.id;
      const next = fullOrdered.map((row) => {
        if (row.id === a) return freeformAttachments[to]!;
        if (row.id === b) return freeformAttachments[from]!;
        return row;
      });
      setBusy(true);
      setErr(null);
      try {
        await tenantApi.contract_attachments.reorder({
          contract_id: contractId,
          ids: next.map((x) => x.id),
        });
        await onChanged();
      } catch (e) {
        setErr((e as Error).message);
      } finally {
        setBusy(false);
      }
    },
    [attachments, freeformAttachments, contractId, onChanged],
  );

  const rename = useCallback(
    async (id: string, displayName: string) => {
      const trimmed = displayName.trim();
      if (!trimmed) return;
      setBusy(true);
      setErr(null);
      try {
        await tenantApi.contract_attachments.update({
          id,
          display_name: trimmed,
        });
        await onChanged();
      } catch (e) {
        setErr((e as Error).message);
      } finally {
        setBusy(false);
      }
    },
    [onChanged],
  );

  const createFromTemplate = useCallback(
    async (templateKey: string, version: string) => {
      setBusy(true);
      setErr(null);
      try {
        const created = await createBilagaFromTemplate(
          contractId,
          templateKey,
          version,
        );
        await onChanged();
        setActive(`att:${created.id}`);
      } catch (e) {
        setErr((e as Error).message);
      } finally {
        setBusy(false);
      }
    },
    [contractId, onChanged],
  );

  const activeTab = tabs.find((t) => t.key === active) ?? tabs[tabs.length - 1]!;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* ── Inner tab strip ──────────────────────────────────────── */}
      <div className="mb-3 flex items-center gap-1 overflow-x-auto border-b border-stone-200 pb-px">
        {tabs.map((t) => {
          if (t.kind === 'add') {
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setActive(t.key)}
                className={
                  'flex shrink-0 items-center gap-1 border-b-2 px-3 py-1.5 text-xs font-medium transition-colors ' +
                  (active === t.key
                    ? 'border-stone-900 text-stone-900'
                    : 'border-transparent text-stone-500 hover:text-stone-700')
                }
              >
                <Plus className="h-3.5 w-3.5" />
                Lägg till bilaga
              </button>
            );
          }
          const label =
            t.kind === 'slot'
              ? (t.attachment?.displayName ?? t.slot.label)
              : t.attachment.displayName;
          const isMissing = t.kind === 'slot' && t.missing;
          const isBlocking = t.kind === 'slot' && t.blocking;
          const isEmptySlot = t.kind === 'slot' && !t.attachment;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setActive(t.key)}
              data-locate-anchor={
                t.kind === 'slot'
                  ? locateAnchor('attachment_slot', t.slot.id)
                  : undefined
              }
              className={
                'flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-1.5 text-xs transition-colors ' +
                (active === t.key
                  ? 'border-stone-900 text-stone-900'
                  : 'border-transparent text-stone-500 hover:text-stone-700')
              }
              title={label}
            >
              <span className="font-medium">Bilaga {t.number}</span>
              <span
                className={
                  'max-w-[10rem] truncate font-normal ' +
                  (isEmptySlot ? 'text-stone-400 italic' : 'text-stone-400')
                }
              >
                · {label}
              </span>
              {isMissing && (
                <span
                  className={
                    'h-1.5 w-1.5 shrink-0 rounded-full ' +
                    (isBlocking ? 'bg-red-500' : 'bg-amber-500')
                  }
                  aria-label="Obligatorisk bilaga saknas"
                />
              )}
            </button>
          );
        })}
      </div>

      {err && (
        <div className="mb-3 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-700">
          {err}
        </div>
      )}

      {/* ── Active tab content ───────────────────────────────────── */}
      {activeTab.kind === 'add' ? (
        <AddBilagaView
          parentTemplateKey={parentTemplateKey}
          attachments={attachments}
          busy={busy}
          disabled={disabled}
          onUpload={uploadFreeform}
          onCreateFromTemplate={createFromTemplate}
        />
      ) : activeTab.kind === 'slot' && !activeTab.attachment ? (
        <EmptySlotView
          slot={activeTab.slot}
          number={activeTab.number}
          missing={activeTab.missing}
          blocking={activeTab.blocking}
          busy={busy}
          disabled={disabled}
          onUpload={(file) => void uploadToSlot(activeTab.slot, file)}
        />
      ) : (
        <BilagaView
          key={activeTab.kind === 'slot' ? activeTab.slot.id : activeTab.attachment.id}
          token={token}
          attachment={
            activeTab.kind === 'slot' ? activeTab.attachment! : activeTab.attachment
          }
          number={activeTab.number}
          contractId={contractId}
          parentValues={parentValues}
          busy={busy}
          disabled={disabled}
          freeform={activeTab.kind === 'freeform' ? activeTab : null}
          freeformCount={freeformAttachments.length}
          onRename={rename}
          onRemove={remove}
          onReorder={reorderFreeform}
          onChanged={onChanged}
          renderTemplateEditor={renderTemplateEditor}
          onReplace={
            activeTab.kind === 'slot'
              ? (file) => void uploadToSlot(activeTab.slot, file)
              : undefined
          }
        />
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── */

function AddBilagaView({
  parentTemplateKey,
  attachments,
  busy,
  disabled,
  onUpload,
  onCreateFromTemplate,
}: {
  parentTemplateKey: string;
  attachments: ReadonlyArray<ContractAttachment>;
  busy: boolean;
  disabled: boolean;
  onUpload: (file: File) => void;
  onCreateFromTemplate: (key: string, version: string) => void;
}): ReactElement {
  const attachmentTemplates = useMemo(() => {
    const out: Array<{ key: string; version: string; module: TemplateModule }> = [];
    for (const [key, versions] of Object.entries(TEMPLATE_REGISTRY)) {
      for (const [version, mod] of Object.entries(versions)) {
        const m = (mod as TemplateModule).metadata;
        if (m.status !== 'active') continue;
        // `'*'` (per ADR-0061) = attachable under any parent (clean bilaga).
        const at = m.attachableTo ?? [];
        if (at.includes('*') || at.includes(parentTemplateKey)) {
          out.push({ key, version, module: mod as TemplateModule });
        }
      }
    }
    return out;
  }, [parentTemplateKey]);

  const existingTemplateKeys = useMemo(
    () =>
      new Set(
        attachments
          .filter((a) => a.kind === 'template' && a.templateKey)
          .map((a) => a.templateKey as string),
      ),
    [attachments],
  );

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) onUpload(file);
  };
  const onDrop = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled || busy) return;
    const file = e.dataTransfer.files?.[0];
    if (file) onUpload(file);
  };

  if (disabled) {
    return (
      <div className="flex flex-1 items-start justify-center overflow-auto p-8">
        <p className="max-w-prose rounded border border-stone-200 bg-white p-6 text-sm leading-relaxed text-stone-600">
          Det här avtalet är inte i utkast-läge, så inga nya bilagor kan läggas
          till. Välj en befintlig bilaga i flikarna ovan för att förhandsgranska
          den.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-5 overflow-auto p-2">
      <div className="mx-auto flex w-full max-w-xl flex-col gap-5">
        <label
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={onDrop}
          className={`flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-stone-300 bg-white px-4 py-10 text-center text-sm text-stone-500 hover:border-stone-400 hover:bg-stone-50 ${busy ? 'pointer-events-none opacity-50' : ''}`}
        >
          <Upload className="h-6 w-6" />
          <span className="font-medium text-stone-700">
            {busy ? 'Laddar upp…' : 'Klicka eller dra en PDF hit'}
          </span>
          <span className="text-xs text-stone-400">
            Läggs till som ny bilaga · Endast PDF, max 25 MB
          </span>
          <input
            type="file"
            accept="application/pdf"
            onChange={onPick}
            className="hidden"
            disabled={busy}
          />
        </label>

        {attachmentTemplates.length > 0 && (
          <div className="flex flex-col gap-2 rounded-lg border border-stone-200 bg-white p-4">
            <h3 className="text-[10px] uppercase tracking-wider text-stone-500">
              Skapa bilaga från mall
            </h3>
            <div className="flex flex-col gap-1.5">
              {attachmentTemplates.map((t) => {
                const m = t.module.metadata;
                const lockedOut =
                  m.singletonPerParent === true &&
                  existingTemplateKeys.has(t.key);
                const label = m.shortTitle ?? m.title;
                return (
                  <button
                    key={`${t.key}@${t.version}`}
                    type="button"
                    disabled={busy || lockedOut}
                    onClick={() => onCreateFromTemplate(t.key, t.version)}
                    className="flex items-start gap-2 rounded border border-stone-200 px-2.5 py-2 text-left text-xs hover:border-stone-400 hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-50"
                    title={
                      lockedOut
                        ? 'Endast en bilaga av denna typ tillåts per avtal.'
                        : undefined
                    }
                  >
                    <FilePlus className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-500" />
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <div className="font-medium text-stone-800">{label}</div>
                      {m.description && (
                        <div className="line-clamp-2 text-[10px] leading-relaxed text-stone-500">
                          {m.description.split('\n')[0]}
                        </div>
                      )}
                      {lockedOut && (
                        <div className="text-[10px] text-stone-500">
                          Redan tillagd
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {attachments.length === 0 && (
          <p className="text-center text-xs text-stone-500">
            Bilagor läggs till den slutliga PDF:en i den ordning de visas i
            flikarna. Använd <span className="font-medium">Visa preliminär
            PDF</span> i topplisten för att se det sammanslagna dokumentet.
          </p>
        )}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── */

function EmptySlotView({
  slot,
  number,
  missing,
  blocking,
  busy,
  disabled,
  onUpload,
}: {
  slot: AttachmentSlot;
  number: number;
  missing: boolean;
  blocking: boolean;
  busy: boolean;
  disabled: boolean;
  onUpload: (file: File) => void;
}): ReactElement {
  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) onUpload(file);
  };
  return (
    <div className="flex flex-1 items-start justify-center overflow-auto p-8">
      <div
        className={
          'flex w-full max-w-md flex-col items-center gap-3 rounded-lg border bg-white p-8 text-center ' +
          (blocking
            ? 'border-red-300'
            : missing
              ? 'border-amber-300'
              : 'border-stone-200')
        }
      >
        <span className="text-xs font-medium uppercase tracking-wider text-stone-500">
          Bilaga {number}
        </span>
        <h3 className="text-sm font-semibold text-stone-800">{slot.label}</h3>
        {slot.help && (
          <p className="text-xs leading-relaxed text-stone-500">{slot.help}</p>
        )}
        {missing && (
          <p
            className={
              'flex items-center gap-1.5 text-xs font-medium ' +
              (blocking ? 'text-red-700' : 'text-amber-700')
            }
          >
            <AlertCircle className="h-3.5 w-3.5" />
            {blocking
              ? 'Krävs innan avtalet kan markeras klart'
              : 'Obligatorisk bilaga — bör laddas upp'}
          </p>
        )}
        {!disabled ? (
          <label
            className={`mt-1 flex cursor-pointer items-center gap-1.5 rounded border border-stone-300 bg-white px-3 py-1.5 text-xs text-stone-700 hover:border-stone-400 hover:bg-stone-50 ${busy ? 'pointer-events-none opacity-50' : ''}`}
          >
            <Upload className="h-3.5 w-3.5" />
            {busy ? 'Laddar upp…' : 'Ladda upp PDF'}
            <input
              type="file"
              accept="application/pdf"
              onChange={onPick}
              className="hidden"
              disabled={busy}
            />
          </label>
        ) : (
          <p className="text-xs text-stone-400">Ingen bilaga uppladdad.</p>
        )}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── */

/**
 * A bound bilaga: header (number + editable name + actions) and a body that
 * is either an embedded PDF (uploads) or an inline template editor.
 */
function BilagaView({
  token,
  attachment,
  number,
  contractId,
  parentValues,
  busy,
  disabled,
  freeform,
  freeformCount,
  onRename,
  onRemove,
  onReorder,
  onChanged,
  onReplace,
  renderTemplateEditor,
}: {
  token: string;
  attachment: ContractAttachment;
  number: number;
  contractId: string;
  parentValues: Record<string, unknown>;
  busy: boolean;
  disabled: boolean;
  freeform: { freeIndex: number } | null;
  freeformCount: number;
  onRename: (id: string, name: string) => void;
  onRemove: (id: string) => void;
  onReorder: (from: number, to: number) => void;
  onChanged: () => Promise<void> | void;
  onReplace?: (file: File) => void;
  renderTemplateEditor?: TemplateEditorRenderer;
}): ReactElement {
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(attachment.displayName);

  useEffect(() => {
    setRenaming(false);
    setDraft(attachment.displayName);
  }, [attachment.id, attachment.displayName]);

  const onReplacePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file && onReplace) onReplace(file);
  };

  const commitRename = () => {
    onRename(attachment.id, draft);
    setRenaming(false);
  };

  const customEditor =
    attachment.kind === 'template' ? (renderTemplateEditor?.(attachment, { disabled, onChanged }) ?? null) : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Header */}
      <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded border border-stone-200 bg-stone-50 px-3 py-2">
        <span className="shrink-0 text-xs font-semibold uppercase tracking-wider text-stone-500">
          Bilaga {number}
        </span>
        {renaming ? (
          <div className="flex flex-1 items-center gap-1">
            <input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitRename();
                if (e.key === 'Escape') setRenaming(false);
              }}
              className="min-w-0 flex-1 rounded border border-stone-300 bg-white px-2 py-1 text-sm"
            />
            <button
              type="button"
              onClick={commitRename}
              className="rounded p-1 text-green-700 hover:bg-green-100"
              title="Spara namn"
            >
              <Check className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setRenaming(false)}
              className="rounded p-1 text-stone-500 hover:bg-stone-200"
              title="Avbryt"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => !disabled && setRenaming(true)}
            disabled={disabled}
            className="flex min-w-0 flex-1 items-center gap-1.5 text-left text-sm font-medium text-stone-800 hover:text-stone-950 disabled:cursor-default"
            title={disabled ? '' : 'Byt namn'}
          >
            <span className="truncate">{attachment.displayName}</span>
            {!disabled && <Pencil className="h-3 w-3 shrink-0 text-stone-400" />}
          </button>
        )}

        <div className="flex shrink-0 items-center gap-1">
          {freeform && !disabled && (
            <>
              <button
                type="button"
                onClick={() => onReorder(freeform.freeIndex, freeform.freeIndex - 1)}
                disabled={freeform.freeIndex === 0 || busy}
                className="rounded p-1 text-stone-500 hover:bg-stone-200 disabled:opacity-30"
                title="Flytta upp"
              >
                <ChevronUp className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => onReorder(freeform.freeIndex, freeform.freeIndex + 1)}
                disabled={freeform.freeIndex >= freeformCount - 1 || busy}
                className="rounded p-1 text-stone-500 hover:bg-stone-200 disabled:opacity-30"
                title="Flytta ned"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </>
          )}
          {attachment.kind === 'upload' && onReplace && !disabled && (
            <label
              className={`flex cursor-pointer items-center gap-1 rounded p-1 text-stone-500 hover:bg-stone-200 hover:text-stone-700 ${busy ? 'pointer-events-none opacity-50' : ''}`}
              title="Ersätt PDF"
            >
              <Upload className="h-4 w-4" />
              <input
                type="file"
                accept="application/pdf"
                onChange={onReplacePick}
                className="hidden"
                disabled={busy}
              />
            </label>
          )}
          {!disabled && (
            <button
              type="button"
              onClick={() => onRemove(attachment.id)}
              disabled={busy}
              className="rounded p-1 text-stone-400 hover:bg-red-100 hover:text-red-700"
              title="Ta bort bilaga"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      {customEditor ? (
        customEditor
      ) : attachment.kind === 'template' ? (
        <TemplateBilagaEditor
          token={token}
          attachment={attachment}
          contractId={contractId}
          parentValues={parentValues}
          disabled={disabled}
          onChanged={onChanged}
        />
      ) : (
        <UploadedBilagaPreview attachment={attachment} />
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── */

/** Embedded inline preview of an uploaded PDF bilaga (T-03). */
function UploadedBilagaPreview({
  attachment,
}: {
  attachment: ContractAttachment;
}): ReactElement {
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    setErr(null);
    (async () => {
      try {
        const r = await tenantApi.contract_attachments.download_url({
          id: attachment.id,
        });
        if (!cancelled) setUrl(r.url);
      } catch (e) {
        if (!cancelled) setErr((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attachment.id]);

  return (
    <div className="flex min-h-0 flex-1 flex-col rounded border border-stone-200 bg-stone-100">
      <div className="flex items-center justify-between gap-2 border-b border-stone-200 px-3 py-1.5 text-[11px] text-stone-500">
        <span className="truncate" title={attachment.uploadFilename ?? ''}>
          {attachment.uploadFilename}
          {attachment.uploadBytes != null && (
            <span className="text-stone-400">
              {' '}
              · {formatBytes(attachment.uploadBytes)}
            </span>
          )}
        </span>
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 hover:bg-stone-200 hover:text-stone-700"
            title="Öppna i ny flik"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Öppna
          </a>
        )}
      </div>
      {err ? (
        <div className="p-4 text-xs text-red-700">
          Kunde inte ladda förhandsvisningen: {err}
        </div>
      ) : url ? (
        <iframe
          src={url}
          title={attachment.displayName}
          className="min-h-[60vh] flex-1 border-0 bg-white"
        />
      ) : (
        <div className="p-4 text-xs text-stone-500">Laddar förhandsvisning…</div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── */

/**
 * Inline editor for a `kind='template'` bilaga — fields on the left, live
 * preview on the right, mirroring the Avtal page. Local-buffer + explicit
 * Save (the parent contract row is already persisted; this writes the
 * attachment's `values` blob). "Synka från avtal" re-snapshots from the
 * parent.
 */
function TemplateBilagaEditor({
  token,
  attachment,
  contractId,
  parentValues,
  disabled,
  onChanged,
}: {
  token: string;
  attachment: ContractAttachment;
  contractId: string;
  parentValues: Record<string, unknown>;
  disabled: boolean;
  onChanged: () => Promise<void> | void;
}): ReactElement {
  const [values, setValues] = useState<Record<string, unknown>>(
    () => ({ ...((attachment.values ?? {}) as Record<string, unknown>) }),
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setValues({ ...((attachment.values ?? {}) as Record<string, unknown>) });
    setDirty(false);
    setErr(null);
  }, [attachment.id, attachment.values]);

  const mod = useMemo(() => {
    if (!attachment.templateKey || !attachment.templateVersion) return null;
    return getTemplateVersion(attachment.templateKey, attachment.templateVersion);
  }, [attachment.templateKey, attachment.templateVersion]);

  if (!mod) {
    return (
      <div className="rounded border border-red-300 bg-red-50 p-4 text-sm text-red-700">
        Mall {attachment.templateKey ?? '?'}@{attachment.templateVersion ?? '?'}{' '}
        finns inte i registret.
      </div>
    );
  }

  const fields = mod.variableSchema as ReadonlyArray<
    VariableField<Record<string, unknown>>
  >;
  const sections = Array.from(new Set(fields.map((f) => f.section)));

  const setValue = (key: string, next: unknown) => {
    setValues((prev) => ({ ...prev, [key]: next }));
    setDirty(true);
  };

  // Values with parent-derived fields overlaid live (ADR-0028 addendum
  // 2026-05-27) — this is what the preview renders and what a locked
  // field shows.
  const effective = applyDerivedFromParent(fields, parentValues, values);

  const unlockDerived = (f: VariableField<Record<string, unknown>>) => {
    setValues((prev) => ({
      ...prev,
      [overrideKey(f.key)]: true,
      [f.key]: effective[f.key],
    }));
    setDirty(true);
  };
  const relockDerived = (f: VariableField<Record<string, unknown>>) => {
    setValues((prev) => {
      const next = { ...prev };
      delete next[overrideKey(f.key)];
      next[f.key] = parentValues[f.derivedFrom ?? f.key];
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await tenantApi.contract_attachments.update_template_values({
        id: attachment.id,
        values,
      });
      await onChanged();
      setDirty(false);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const resync = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await tenantApi.contract_attachments.resync_from_parent({
        id: attachment.id,
      });
      setValues(r.values);
      await onChanged();
      setDirty(false);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-0 flex-1 grid-cols-1 gap-6 lg:grid-cols-[420px_1fr]">
      <aside className="flex flex-col gap-4 overflow-auto">
        {err && (
          <div className="rounded border border-red-300 bg-red-50 p-2 text-xs text-red-700">
            {err}
          </div>
        )}
        {sections.map((section) => (
          <div key={section}>
            <h3 className="mb-2 border-b border-stone-200 pb-1 text-xs font-medium uppercase tracking-wider text-stone-500">
              {section}
            </h3>
            <div className="flex flex-col gap-3">
              {fields
                .filter((f) => f.section === section)
                .filter((f) => evaluateVisibilityRule(f.visibleWhen, values))
                .map((f) => {
                  const Renderer = RENDERERS[f.type];
                  const locked = isDerivedLocked(f, values);
                  return (
                    <div key={f.key} className="flex flex-col gap-1">
                      <Renderer
                        field={f}
                        value={locked ? effective[f.key] : values[f.key]}
                        allValues={effective}
                        dataTableXlsx={dataTableXlsxIO}
                        onChange={(next) => setValue(f.key, next)}
                        disabled={disabled || busy || locked}
                      />
                      {f.derivedFromParent === true && !disabled && (
                        <div className="flex items-center gap-1.5 text-[10px] text-stone-500">
                          {locked ? (
                            <>
                              <Lock className="h-3 w-3" />
                              <span>Hämtas från avtalet</span>
                              <button
                                type="button"
                                onClick={() => unlockDerived(f)}
                                className="ml-auto flex items-center gap-1 rounded px-1.5 py-0.5 text-stone-600 hover:bg-stone-100 hover:text-stone-800"
                              >
                                <Pencil className="h-3 w-3" />
                                Ändra ändå
                              </button>
                            </>
                          ) : (
                            <>
                              <Pencil className="h-3 w-3 text-amber-600" />
                              <span className="text-amber-700">
                                Frikopplad från avtalet
                              </span>
                              <button
                                type="button"
                                onClick={() => relockDerived(f)}
                                className="ml-auto flex items-center gap-1 rounded px-1.5 py-0.5 text-stone-600 hover:bg-stone-100 hover:text-stone-800"
                              >
                                <RotateCw className="h-3 w-3" />
                                Återställ till avtalet
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        ))}

        {/* Custom sections (ADR-0060/0059) — for the clean bilaga this is the
            whole body. Frame-less ⇒ no placement dropdown; sections number 1..N. */}
        {templateSupportsCustomSections(mod) && (
          <div>
            <h3 className="mb-2 border-b border-stone-200 pb-1 text-xs font-medium uppercase tracking-wider text-stone-500">
              Innehåll
            </h3>
            <CustomSectionsEditor
              sections={readCustomSections(values, 'blank')}
              numbering={computeSectionNumbers(
                mod.metadata.sections ?? [],
                values,
                'final',
              )}
              placementOptions={(mod.metadata.sections ?? [])
                .map((s) => ({
                  ref: s.ref,
                  num: computeSectionNumbers(
                    mod.metadata.sections ?? [],
                    values,
                    'final',
                  ).get(s.ref),
                  title: s.title,
                }))
                .filter((o): o is { ref: string; num: number; title: string } =>
                  typeof o.num === 'number',
                )
                .map((o) => ({ ref: o.ref, label: `§${o.num} ${o.title}` }))}
              defaultAfterRef={undefined}
              disabled={disabled || busy}
              onChange={(next) => setValue(CUSTOM_SECTIONS_KEY, next)}
            />
          </div>
        )}

        {!disabled && (
          <div className="sticky bottom-0 flex items-center gap-2 border-t border-stone-200 bg-white pt-3">
            <button
              type="button"
              onClick={() => void save()}
              disabled={busy || !dirty}
              className="rounded bg-stone-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-stone-800 disabled:opacity-50"
            >
              {busy ? 'Sparar…' : dirty ? 'Spara' : 'Sparat'}
            </button>
            <button
              type="button"
              onClick={() => void resync()}
              disabled={busy}
              className="flex items-center gap-1 rounded border border-stone-300 px-2.5 py-1.5 text-xs text-stone-700 hover:bg-stone-50 disabled:opacity-50"
              title="Hämta om uppgifter från avtalet (skriver över snapshotvärden)"
            >
              <RotateCw className="h-3.5 w-3.5" />
              Synka från avtal
            </button>
          </div>
        )}
      </aside>

      <main className="flex min-h-0 flex-col overflow-hidden bg-stone-100">
        <div className="overflow-auto p-4">
          <DocumentMetaProvider
            value={{
              avtalNr: contractId,
              objektNr: resolveObjektNr(
                mod.metadata.titleSuffix?.key,
                { ...parentValues, ...effective },
                mod.metadata.titleSuffix?.fallbackKey,
              ),
            }}
          >
            <mod.Component values={effective} mode="final" />
          </DocumentMetaProvider>
        </div>
      </main>
    </div>
  );
}
